import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';

import { authTokenStorageKey } from 'hooks/useAuthentication';
import { useStorage } from 'hooks/useStorage';

import { axiosFactory } from 'utils/http/axiosFactory';
import { sendToErrorTracking } from 'utils/sendToErrorTracking';

interface AppointmentTermsCtx {
  checkboxStatus: boolean;
  setCheckboxStatus: Dispatch<SetStateAction<boolean>>;
  setAppointmentTermsAccepted: (appointmentId?: string) => Promise<void>;
}

const AppointmentTermsContext = createContext({} as AppointmentTermsCtx);

export const AppointmentTermsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { i18n } = useTranslation();
  const [checkboxStatus, setCheckboxStatus] = useState(false);
  const { get } = useStorage();
  const router = useRouter();

  const setAppointmentTermsAccepted = useCallback(
    async (appointmentId?: string) => {
      if (!appointmentId) return;

      const fetcher = axiosFactory(i18n.language, 'attendence');

      const authorization = `Bearer ${JSON.parse(get(authTokenStorageKey))}`;

      try {
        await fetcher.put(
          `/appointment/${appointmentId}/custom`,
          {
            appointmentTermAccepted: true,
          },
          {
            headers: {
              Authorization: authorization,
            },
          },
        );
      } catch (error) {
        sendToErrorTracking({
          error,
          where: 'AppointmentTermsProvider/setAppointmentTermsAccepted',
          context: appointmentId,
          showErrorObj: false,
        });

        router.push('/500');
      }
    },
    [get, i18n.language, router],
  );

  const providerProps = useMemo(
    () => ({
      checkboxStatus,
      setCheckboxStatus,
      setAppointmentTermsAccepted,
    }),
    [checkboxStatus, setAppointmentTermsAccepted],
  );

  return (
    <AppointmentTermsContext.Provider value={providerProps}>
      {children}
    </AppointmentTermsContext.Provider>
  );
};

export const useAppointmentTerms = () => {
  const context = useContext(AppointmentTermsContext);

  if (!context) {
    throw new Error('useCoupon hook must be used under CouponProvider');
  }

  return context;
};
